import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import date from "date-and-time";

import Modal from "../../layouts/Modal";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { Alert } from "../../components/base/Alert";
import useTicket from "../../data/Ticket/useTicket";
import useCustomers from "../../data/Customer/useCustomers";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import useTicketMutations from "../../data/Ticket/useTicketMutations";
import useAuth from "../../hooks/useAuth";
import useTicketAttachments from "../../data/Ticket/useTicketAttachments";
import { ITicketAttachment, TicketType } from "../../models/Tickets/Ticket";
import { errorResolver } from "../../utils/errorResolver";
import TicketChatThread from "./TicketChatThread";
import TicketFeatureDecisionMenu from "./TicketFeatureDecisionMenu";
import TicketImagePreviewModal from "./TicketImagePreviewModal";
import TicketPriorityMenu from "./TicketPriorityMenu";
import { TicketStatusPill, TicketTypePill } from "./ticketMeta";

interface TicketPeekModalProps {
  ticketId: string | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TicketPeekModal({
  ticketId,
  open,
  setOpen,
}: TicketPeekModalProps) {
  const { ticket } = useTicket(ticketId ?? "");
  const { customers } = useCustomers();
  const { customerUsers } = useCustomerUsers(ticket.data?.customerId ?? "");
  const { addNote, assignToMe } = useTicketMutations();
  const { user } = useAuth();

  const customerName = customers.data?.find(
    (c) => c.id === ticket.data?.customerId,
  )?.name;

  // Matches the ticket page: name the person who raised it, falling back to the
  // raw id when they aren't in the customer's user list.
  const createdByLabel = (() => {
    if (!ticket.data) return "";
    const raiser = customerUsers.data?.find(
      (x) => x.id === ticket.data?.createdByUserId,
    );
    if (raiser) {
      return `${raiser.forename} ${raiser.surname}`.trim() || raiser.username;
    }
    return ticket.data.createdByUserId;
  })();

  const [noteText, setNoteText] = useState("");
  const [activeImagePreview, setActiveImagePreview] =
    useState<ITicketAttachment | null>(null);

  const { previewUrls, downloadAttachment } = useTicketAttachments(
    ticket.data?.id,
    ticket.data?.attachments,
  );

  const internalNotes = useMemo(
    () =>
      [...(ticket.data?.notes ?? [])]
        .filter((n) => n.isInternal)
        .sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        ),
    [ticket.data?.notes],
  );

  const handleAddNote = (isInternal: boolean) => {
    if (!ticket.data || !noteText.trim()) return;

    addNote.mutate(
      {
        ticketId: ticket.data.id,
        note: {
          note: noteText.trim(),
          isInternal,
          userId: ticket.data.createdByUserId,
        },
      },
      { onSuccess: () => setNoteText("") },
    );
  };

  const isAssignedToMe =
    !!user?.email &&
    ticket.data?.assignedToUserId?.toLowerCase() === user.email.toLowerCase();

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      {!ticketId || ticket.isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingWheel />
        </div>
      ) : ticket.isError ? (
        <Alert
          severity="error"
          title="Error loading ticket"
          message={errorResolver(ticket.error)}
        />
      ) : !ticket.data ? (
        <Alert
          severity="warn"
          title="Ticket not found"
          message="This ticket no longer exists."
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="truncate text-lg font-semibold text-slate-800">
                {ticket.data.subject}
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                {customerName ? `${customerName} - ` : ""}
                {date.format(new Date(ticket.data.created), "DD/MM/YYYY HH:mm")}
              </p>
            </div>
            {/* Priority and, for features, the decision sit top right and are editable in place. */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <TicketTypePill type={ticket.data.type} />
              <TicketStatusPill status={ticket.data.status} />
              <TicketPriorityMenu
                ticketId={ticket.data.id}
                priority={ticket.data.priority}
              />
              {ticket.data.type === TicketType.Feature ? (
                <TicketFeatureDecisionMenu
                  ticketId={ticket.data.id}
                  decision={ticket.data.featureDecision}
                  align="right"
                />
              ) : null}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs text-gray-500">Conversation</p>
            <TicketChatThread
              notes={ticket.data.notes ?? []}
              opening={{
                author: createdByLabel || customerName || "Customer",
                subject: ticket.data.subject,
                description: ticket.data.description,
                created: ticket.data.created,
              }}
              attachments={ticket.data.attachments}
              attachmentPreviewUrls={previewUrls}
              onAttachmentClick={setActiveImagePreview}
              onAttachmentDownload={downloadAttachment}
              className="max-h-[40vh]"
              emptyMessage="No public replies yet."
            />
          </div>

          {/* Internal notes sit outside the chat thread, same as the ticket page. */}
          {internalNotes.length > 0 ? (
            <div>
              <p className="mb-2 text-xs text-gray-500">Internal notes</p>
              <div className="max-h-40 space-y-2 overflow-y-auto">
                {internalNotes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-lg border border-purple-100 bg-purple-50/80 px-2.5 py-2 text-xs"
                  >
                    <p className="mb-1 text-[10px] text-purple-800/80">
                      {note.authorName ?? note.authorEmail ?? "Staff"} ·{" "}
                      {date.format(new Date(note.created), "DD/MM/YYYY HH:mm")}
                    </p>
                    <p className="whitespace-pre-wrap text-slate-800">
                      {note.note}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="space-y-2 border-t pt-3">
            <textarea
              className="min-h-20 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              placeholder="Write a reply..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => handleAddNote(false)}
                disabled={addNote.isPending || !noteText.trim()}
              >
                {addNote.isPending ? "Sending..." : "Send reply"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleAddNote(true)}
                disabled={addNote.isPending || !noteText.trim()}
              >
                Internal note
              </Button>
              <Button
                variant="outlined"
                onClick={() => assignToMe.mutate(ticket.data!.id)}
                disabled={assignToMe.isPending || isAssignedToMe}
              >
                {isAssignedToMe ? "Assigned to you" : "Allocate to me"}
              </Button>
            </div>
            {ticket.data.assignedToUserId && !isAssignedToMe ? (
              <p className="text-xs text-gray-500">
                Currently assigned to {ticket.data.assignedToUserId}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <Link
              to={`/tickets/${ticket.data.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-orange-700 hover:text-orange-900"
            >
              Open full ticket
            </Link>
            <Button variant="outlined" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>

          {/* Lives inside the panel so clicking it doesn't dismiss the peek modal. */}
          <TicketImagePreviewModal
            attachment={activeImagePreview}
            src={
              activeImagePreview ? previewUrls[activeImagePreview.id] : undefined
            }
            onClose={() => setActiveImagePreview(null)}
            onDownload={downloadAttachment}
          />
        </div>
      )}
    </Modal>
  );
}
